import { Connection } from "../clases/Connection";
import { Values } from "../clases/Values";
import { Line } from "../clases/Line";
import { Circle } from "../clases/Circle";
import { Shape } from "../enums/Shape";
import { AbstractShape } from "./Shape";
import { ConnectionRenderer } from "./ConnectionRenderer";

type Axis = 'horizontal' | 'vertical';
type AbsoluteBounds = { left: number, right: number, top: number, bottom: number };
type ResolvedAnchor = { point: Values, index: number };
type ResolvedConnectionAnchors = { from: ResolvedAnchor, to: ResolvedAnchor };

export abstract class Arrow {
    constructor(){}

    line1: Values = new Values(0, 0);
    middleLine: Values = new Values(0, 0);
    line2: Values = new Values(0, 0);
    strc: Values = new Values(0, 0);
    endc: Values = new Values(0, 0);
    squarePath: Values[] = [];
    squareDrawPath: Values[] = [];

    calculateMiddleText(connection: ConnectionRenderer, bounding: any){
        let strc = this.getCords(connection, connection.iFrom, true);
        let endc = this.getCords(connection, connection.iTo!, false);

        return this.calculateMiddleTextWithPoints(bounding, strc, endc);
    }

    calculateMiddleTextWithPoints(bounding: any, start: Values, end: Values){
        let { x, y } = this.calculateMiddlePoint(start, end);

        return this.calculateTextPositionFromPoint(
            bounding,
            new Values(x, y)
        );
    }

    calculateTextWithPercentage(bounding: any, points: Values[], percentage: number){
        return this.calculateTextPositionFromPoint(
            bounding,
            this.calculatePointOnPath(points, percentage)
        );
    }

    calculateTextPositionFromPoint(bounding: any, point: Values) {
        let middleX = point.x - (bounding.width/2);
        let middleY = point.y - (bounding.height/2);

        return { x: middleX, y: middleY };
    }

    calculatePointOnPath(points: Values[], percentage: number): Values {
        if (!points.length) {
            return new Values(0, 0);
        }

        if (points.length === 1) {
            return new Values(points[0].x, points[0].y);
        }

        const normalizedPercentage = Math.max(0, Math.min(100, percentage));
        const targetDistance = this.getPathLength(points) * (normalizedPercentage / 100);

        if (targetDistance <= 0) {
            return new Values(points[0].x, points[0].y);
        }

        let currentDistance = 0;
        for (let index = 0; index < points.length - 1; index++) {
            const start = points[index];
            const end = points[index + 1];
            const segmentDistance = this.distanceBetween2Points(start, end);

            if (segmentDistance === 0) continue;

            if (currentDistance + segmentDistance >= targetDistance) {
                const ratio = (targetDistance - currentDistance) / segmentDistance;
                return new Values(
                    start.x + ((end.x - start.x) * ratio),
                    start.y + ((end.y - start.y) * ratio),
                );
            }

            currentDistance += segmentDistance;
        }

        const lastPoint = points[points.length - 1];
        return new Values(lastPoint.x, lastPoint.y);
    }

    getPathLength(points: Values[]): number {
        let totalDistance = 0;
        for (let index = 0; index < points.length - 1; index++) {
            totalDistance += this.distanceBetween2Points(points[index], points[index + 1]);
        }

        return totalDistance;
    }

    getTextWidth(text: string, fontSize: number, fontFamily: string, ctx: CanvasRenderingContext2D, scale: number = 20) {
        ctx!.font = `${fontSize}px ${fontFamily}`;
        const textMetrics = ctx!.measureText(text);
        return {
            width: textMetrics.width,
            height: fontSize - scale
        };
    }

    calculateSquareArrow(connection: ConnectionRenderer){
        const anchors = this.resolveConnectionAnchors(connection);
        this.strc = anchors.from.point;
        this.endc = anchors.to.point;

        const deltaX = this.endc.x - this.strc.x;
        const deltaY = this.endc.y - this.strc.y;
        const svgWidth = Math.abs(deltaX);
        const svgHeight = Math.abs(deltaY);
        const orientation = this.getSquareOrientation(anchors, svgWidth, svgHeight);
        const dirX = this.normalizeDirection(deltaX);
        const dirY = this.normalizeDirection(deltaY);

        this.buildSquareSegments(orientation, svgWidth, svgHeight, dirX, dirY);

        this.makeAllIntegers();
        this.squarePath = [
            new Values(this.strc.x, this.strc.y),
            new Values(this.line1.x, this.line1.y),
            new Values(this.middleLine.x, this.middleLine.y),
            new Values(this.line2.x, this.line2.y),
        ];

        const recess = Math.max(Math.ceil(connection.scale * 2), 3);
        const { startLine, lastLine } = this.trimSquareSegments(orientation, dirX, dirY, recess);
        this.squareDrawPath = [ startLine, this.line1, this.middleLine, lastLine ];
        return this.squareDrawPath;
    }

    calculateLArrow(connection: ConnectionRenderer){
        const anchors = this.resolveConnectionAnchors(connection);
        this.strc = anchors.from.point;
        this.endc = anchors.to.point;

        const fromBounds = this.getAbsoluteBounds(connection.iFrom, new Values(connection.x1, connection.y1));
        const toBounds = this.getAbsoluteBounds(connection.iTo!, new Values(connection.x2, connection.y2));
        const basePadding = Math.max(Math.ceil(connection.scale * 8), 12);
        const horizontalSpacing = connection.lArrowHorizontalSpacing;
        const verticalSpacing = connection.lArrowVerticalSpacing;
        const horizontalPadding = basePadding + Math.abs(horizontalSpacing);
        const verticalPadding = basePadding + Math.abs(verticalSpacing);

        const preferredPath = this.selectFirstClearPath(
            this.buildLPathCandidates(
                this.strc,
                anchors.from.index,
                fromBounds,
                this.endc,
                anchors.to.index,
                toBounds,
                basePadding,
                horizontalPadding,
                verticalPadding,
                horizontalSpacing,
                verticalSpacing
            ),
            fromBounds,
            toBounds
        );

        const resolvedPath = preferredPath || this.buildOrthogonalPath(
                this.strc,
                anchors.from.index,
                fromBounds,
                this.endc,
                anchors.to.index,
                toBounds,
                horizontalPadding,
                verticalPadding
            );

        this.squarePath = resolvedPath.map((point) => new Values(Math.ceil(point.x), Math.ceil(point.y)));

        this.syncSquareSegments();

        const recess = Math.max(Math.ceil(connection.scale * 2), 3);
        this.squareDrawPath = this.trimPathForArrowHeads(this.squarePath, recess);

        return this.squareDrawPath;
    }

    makeAllIntegers(){
        this.strc = new Values(Math.ceil(this.strc.x), Math.ceil(this.strc.y));
        this.line1 = new Values(Math.ceil(this.line1.x), Math.ceil(this.line1.y));
        this.middleLine = new Values(Math.ceil(this.middleLine.x), Math.ceil(this.middleLine.y));
        this.line2 = new Values(Math.ceil(this.line2.x), Math.ceil(this.line2.y));
        this.squarePath = this.squarePath.map((point) => new Values(Math.ceil(point.x), Math.ceil(point.y)));
        this.squareDrawPath = this.squareDrawPath.map((point) => new Values(Math.ceil(point.x), Math.ceil(point.y)));
    }

    calculateMiddlePoint(v1: Values, v2: Values): Values{
        const middleX = (v1.x + v2.x) /2;
        const middleY = (v1.y + v2.y) /2;
        return { x: middleX, y: middleY };
    }

    orientation(connection: ConnectionRenderer){
        let strc = this.getCords(connection, connection.iFrom, true);
        let endc = this.getCords(connection, connection.iTo!, false);
        let dx = endc.x - strc.x;
        let dy = endc.y - strc.y;
        let radians = Math.atan2(dy, dx);
        let degrees = Math.round((radians * 180 / Math.PI + 360) % 360);
        return degrees + "deg";
    }

    determineCoords(item: AbstractShape, p1: Values, p2: Values, center: Values): Values{
        let inter = center;
        // if(item.shape === Shape.CIRCLE || item.shape === Shape.SIMPLE_CIRCLE){
        //this is a additional I implemented for temporal fixes, check later
        if(item.shape === Shape.CIRCLE){
            let circle = item.toCircle(center);
            inter = this.findLineCircleIntersections(circle, new Line(p1, p2))[0];
        }else{
            inter = this.getIntersection(item, p1, p2, center) || center;
        }
        return inter;
    }

    getCords(connection: ConnectionRenderer, item: AbstractShape, start: boolean): Values {
        let centerItem = item!.findItemCenter();
        const p1 = {x: connection.x1, y: connection.y1 } as Values;
        const p2 = {x: connection.x2, y: connection.y2 } as Values;
        const center = start ? p1 : p2;
        center.x = center.x + centerItem.x;
        center.y = center.y + centerItem.y;
        let inter = this.determineCoords(item, p1, p2, center) || center;
        return inter;
    }

    getIntersection(Item: AbstractShape, p1: Values, p2: Values, center: Values): Values | null {
        let boxLines = Item.getShapeLines(center);

        for (const lines of boxLines) {
            let p3 = lines.start;
            let p4 = lines.end;
            let intersect = this.line_intersect(p1.x, p1.y, p2.x, p2.y, p3.x, p3.y, p4.x, p4.y);
            if(!intersect) continue;
            return { x: intersect.x, y: intersect.y } as Values;
        }
        return center;
    }

    line_intersect(x1: number, y1: number, x2: number, y2: number, x3: number, y3: number, x4: number, y4: number){
        let ua, ub, denom = ((y4 - y3) * (x2 - x1)) - ((x4 - x3) * (y2 - y1));
        if (denom == 0) {
            return null;
        }
        ua = (((x4 - x3) * (y1 - y3)) - ((y4 - y3) * (x1 - x3))) / denom;
        ub = (((x2 - x1) * (y1 - y3)) - ((y2 - y1) * (x1 - x3))) / denom;
        if (ua < 0 || ua > 1 || ub < 0 || ub > 1) {
            return null;
        }
        return {
            x: x1 + ua * (x2 - x1),
            y: y1 + ua * (y2 - y1),
            seg1: ua >= 0 && ua <= 1,
            seg2: ub >= 0 && ub <= 1
        }
    }

    findLineCircleIntersections(circle: Circle, line: Line): Values[] {
        const { x: cx, y: cy, radius } = circle;
        const { start, end } = line;
        const { x: x1, y: y1 } = start;
        const { x: x2, y: y2 } = end;

        // Line equation: P(t) = (x1 + t * dx, y1 + t * dy)
        const dx = x2 - x1;
        const dy = y2 - y1;

        // Circle equation: (px - cx)^2 + (py - cy)^2 = radius^2
        // Substitute line equation into circle equation:
        const a = dx * dx + dy * dy;
        const b = 2 * (dx * (x1 - cx) + dy * (y1 - cy));
        const c = (x1 - cx) * (x1 - cx) + (y1 - cy) * (y1 - cy) - radius * radius;

        // Quadratic discriminant
        const discriminant = b * b - 4 * a * c;

        // No intersection
        if (discriminant < 0) {
            return [];
        }

        // Calculate t values
        const t1 = (-b + Math.sqrt(discriminant)) / (2 * a);
        const t2 = (-b - Math.sqrt(discriminant)) / (2 * a);

        const intersections: Values[] = [];

        // Check if t1 is within the line segment
        if (t1 >= 0 && t1 <= 1) {
            intersections.push({
                x: x1 + t1 * dx,
                y: y1 + t1 * dy,
            });
        }

        // Check if t2 is within the line segment
        if (t2 >= 0 && t2 <= 1) {
            intersections.push({
                x: x1 + t2 * dx,
                y: y1 + t2 * dy,
            });
        }

        return intersections;
    }

    isPointNearLine(point: Values, line: Line, margin: number): boolean {
        const { x: x1, y: y1 } = line.start;
        const { x: x2, y: y2 } = line.end;
        const { x: px, y: py } = point;
      
        // Compute line vector and point vector
        const lineVectorX = x2 - x1;
        const lineVectorY = y2 - y1;
        const pointVectorX = px - x1;
        const pointVectorY = py - y1;
      
        // Calculate the line length squared
        const lineLengthSquared = lineVectorX ** 2 + lineVectorY ** 2;
        if (lineLengthSquared === 0) {
            return false;
        }
      
        // Project point vector onto line vector to find the projection parameter t
        const t = Math.max(0, Math.min(1, (pointVectorX * lineVectorX + pointVectorY * lineVectorY) / lineLengthSquared));
      
        // Find the closest point on the line segment to the point
        const closestX = x1 + t * lineVectorX;
        const closestY = y1 + t * lineVectorY;
      
        // Calculate the distance from the point to the closest point on the line
        const distanceSquared = (px - closestX) ** 2 + (py - closestY) ** 2;
      
        // Check if the distance is within the margin
        return distanceSquared <= margin ** 2;
    }

    distanceBetween2Points(point1: Values, point2: Values) {
        const dx = point2.x - point1.x;
        const dy = point2.y - point1.y;
        return Math.sqrt(dx ** 2 + dy ** 2);
    }

    getBestTextSegment(points: Values[]): { start: Values, end: Values } {
        if (points.length < 2) {
            return { start: this.strc, end: this.endc };
        }

        let bestIndex = 0;
        let bestDistance = -1;
        const middleIndex = (points.length - 2) / 2;

        for (let index = 0; index < points.length - 1; index++) {
            const start = points[index];
            const end = points[index + 1];
            const distance = this.distanceBetween2Points(start, end);
            if (distance > bestDistance) {
                bestIndex = index;
                bestDistance = distance;
                continue;
            }

            if (distance === bestDistance) {
                const currentDistance = Math.abs(index - middleIndex);
                if (currentDistance < Math.abs(bestIndex - middleIndex)) {
                    bestIndex = index;
                }
            }
        }

        return { start: points[bestIndex], end: points[bestIndex + 1] };
    }

    private resolveConnectionAnchors(connection: ConnectionRenderer): ResolvedConnectionAnchors {
        return {
            from: connection.iFrom.getBestAnchorPoint(
                connection.iTo!,
                new Values(connection.x1, connection.y1),
                new Values(connection.x2, connection.y2),
                connection.anchorFrom
            ),
            to: connection.iTo!.getBestAnchorPoint(
                connection.iFrom,
                new Values(connection.x2, connection.y2),
                new Values(connection.x1, connection.y1),
                connection.anchorTo
            ),
        };
    }

    private getSquareOrientation(anchors: ResolvedConnectionAnchors, width: number, height: number): Axis {
        if (
            (anchors.from.index === 0 && anchors.to.index === 2) ||
            (anchors.from.index === 2 && anchors.to.index === 0)
        ) {
            return 'vertical';
        }

        if (
            (anchors.from.index === 1 && anchors.to.index === 3) ||
            (anchors.from.index === 3 && anchors.to.index === 1)
        ) {
            return 'horizontal';
        }

        return width > height ? 'horizontal' : 'vertical';
    }

    private normalizeDirection(value: number): number {
        const direction = Math.sign(value);
        return direction === 0 ? 1 : direction;
    }

    private buildSquareSegments(orientation: Axis, width: number, height: number, dirX: number, dirY: number) {
        if (orientation === 'horizontal') {
            this.line1 = new Values(this.strc.x + ((width / 2) * dirX), this.strc.y);
            this.middleLine = new Values(this.line1.x, this.strc.y + (height * dirY));
            this.line2 = new Values(this.endc.x, this.middleLine.y);
            return;
        }

        this.line1 = new Values(this.strc.x, this.strc.y + ((height / 2) * dirY));
        this.middleLine = new Values(this.strc.x + (width * dirX), this.line1.y);
        this.line2 = new Values(this.middleLine.x, this.endc.y);
    }

    private trimSquareSegments(orientation: Axis, dirX: number, dirY: number, recess: number): { startLine: Values, lastLine: Values } {
        const startLine = new Values(this.strc.x, this.strc.y);
        const lastLine = new Values(this.line2.x, this.line2.y);

        if (orientation === 'horizontal') {
            startLine.x = Math.ceil(this.strc.x + (recess * dirX));
            lastLine.x = Math.ceil(this.line2.x - (recess * dirX));
            return { startLine, lastLine };
        }

        startLine.y = Math.ceil(this.strc.y + (recess * dirY));
        lastLine.y = Math.ceil(this.line2.y - (recess * dirY));
        return { startLine, lastLine };
    }

    private buildOrthogonalPath(
        start: Values,
        startAnchorIndex: number,
        startBounds: AbsoluteBounds,
        end: Values,
        endAnchorIndex: number,
        endBounds: AbsoluteBounds,
        horizontalPadding: number,
        verticalPadding: number,
        horizontalSpacing: number = 0,
        verticalSpacing: number = 0
    ): Values[] {
        const startDirection = this.getAnchorDirection(startAnchorIndex);
        const endDirection = this.getAnchorDirection(endAnchorIndex);
        const startStub = this.offsetPoint(start, startDirection, this.getDirectionalPadding(startDirection, horizontalPadding, verticalPadding));
        const endStub = this.offsetPoint(end, endDirection, this.getDirectionalPadding(endDirection, horizontalPadding, verticalPadding));

        const leftCorridor = Math.min(startBounds.left, endBounds.left) - horizontalPadding;
        const rightCorridor = Math.max(startBounds.right, endBounds.right) + horizontalPadding;
        const topCorridor = Math.min(startBounds.top, endBounds.top) - verticalPadding;
        const bottomCorridor = Math.max(startBounds.bottom, endBounds.bottom) + verticalPadding;

        const candidatePaths: Values[][] = [
            [start, startStub, endStub, end],
            [start, startStub, new Values(endStub.x, startStub.y), endStub, end],
            [start, startStub, new Values(startStub.x, endStub.y), endStub, end],
            [start, startStub, new Values(leftCorridor, startStub.y), new Values(leftCorridor, endStub.y), endStub, end],
            [start, startStub, new Values(rightCorridor, startStub.y), new Values(rightCorridor, endStub.y), endStub, end],
            [start, startStub, new Values(startStub.x, topCorridor), new Values(endStub.x, topCorridor), endStub, end],
            [start, startStub, new Values(startStub.x, bottomCorridor), new Values(endStub.x, bottomCorridor), endStub, end],
        ];

        return this.selectBestPath(candidatePaths, startBounds, endBounds);
    }

    private getAbsoluteBounds(item: AbstractShape, position: Values): AbsoluteBounds {
        const bounds = item.currentBounding ? item.currentBounding : item.toShapeBounding();
        return {
            left: position.x,
            right: position.x + bounds.width,
            top: position.y,
            bottom: position.y + bounds.height,
        };
    }

    private getAnchorDirection(anchorIndex: number): Values {
        switch (anchorIndex) {
            case 0:
                return new Values(0, -1);
            case 1:
                return new Values(1, 0);
            case 2:
                return new Values(0, 1);
            case 3:
                return new Values(-1, 0);
            default:
                return new Values(1, 0);
        }
    }

    private getSegmentAxis(start: Values, end: Values): Axis {
        return start.x === end.x ? 'vertical' : 'horizontal';
    }

    private offsetPoint(point: Values, direction: Values, distance: number): Values {
        return new Values(point.x + direction.x * distance, point.y + direction.y * distance);
    }

    private getDirectionalPadding(direction: Values, horizontalPadding: number, verticalPadding: number): number {
        return direction.x !== 0 ? horizontalPadding : verticalPadding;
    }

    private getDirectionAxis(direction: Values): Axis {
        return direction.x !== 0 ? 'horizontal' : 'vertical';
    }

    private buildLPathCandidates(
        start: Values,
        startAnchorIndex: number,
        startBounds: AbsoluteBounds,
        end: Values,
        endAnchorIndex: number,
        endBounds: AbsoluteBounds,
        basePadding: number,
        horizontalPadding: number,
        verticalPadding: number,
        horizontalSpacing: number,
        verticalSpacing: number
    ): Values[][] {
        const startDirection = this.getAnchorDirection(startAnchorIndex);
        const endDirection = this.getAnchorDirection(endAnchorIndex);
        const startAxis = this.getDirectionAxis(startDirection);
        const endAxis = this.getDirectionAxis(endDirection);
        const startStub = this.offsetPoint(start, startDirection, basePadding);
        const endStub = this.offsetPoint(end, endDirection, basePadding);
        const leftCorridor = Math.min(startBounds.left, endBounds.left) - horizontalPadding + verticalSpacing;
        const rightCorridor = Math.max(startBounds.right, endBounds.right) + horizontalPadding + verticalSpacing;
        const topCorridor = Math.min(startBounds.top, endBounds.top) - verticalPadding - horizontalSpacing;
        const bottomCorridor = Math.max(startBounds.bottom, endBounds.bottom) + verticalPadding - horizontalSpacing;
        const defaultGuideX = startAxis === 'vertical'
            ? startStub.x
            : (endAxis === 'vertical' ? endStub.x : endStub.x);
        const defaultGuideY = startAxis === 'vertical'
            ? endStub.y
            : startStub.y;
        const guideX = defaultGuideX + verticalSpacing;
        const guideY = defaultGuideY - horizontalSpacing;
        const xFallbacks = [leftCorridor, rightCorridor]
            .sort((first, second) => Math.abs(first - guideX) - Math.abs(second - guideX));
        const yFallbacks = [topCorridor, bottomCorridor]
            .sort((first, second) => Math.abs(first - guideY) - Math.abs(second - guideY));

        const candidates: Values[][] = [
            startAxis === 'vertical'
                ? this.buildVerticalStartLPath(start, end, startStub, endStub, guideX, guideY)
                : this.buildGuidedLPath(start, end, startStub, startAxis, endStub, endAxis, guideX, guideY),
        ];

        for (const fallbackX of xFallbacks) {
            candidates.push(
                startAxis === 'vertical'
                    ? this.buildVerticalStartLPath(start, end, startStub, endStub, fallbackX, guideY)
                    : this.buildGuidedLPath(start, end, startStub, startAxis, endStub, endAxis, fallbackX, guideY)
            );
        }

        for (const fallbackY of yFallbacks) {
            candidates.push(
                startAxis === 'vertical'
                    ? this.buildVerticalStartLPath(start, end, startStub, endStub, guideX, fallbackY)
                    : this.buildGuidedLPath(start, end, startStub, startAxis, endStub, endAxis, guideX, fallbackY)
            );
        }

        for (const fallbackX of xFallbacks) {
            for (const fallbackY of yFallbacks) {
                candidates.push(
                    startAxis === 'vertical'
                        ? this.buildVerticalStartLPath(start, end, startStub, endStub, fallbackX, fallbackY)
                        : this.buildGuidedLPath(start, end, startStub, startAxis, endStub, endAxis, fallbackX, fallbackY)
                );
            }
        }

        return candidates;
    }

    private buildGuidedLPath(
        start: Values,
        end: Values,
        startStub: Values,
        startAxis: Axis,
        endStub: Values,
        endAxis: Axis,
        bendX: number,
        bendY: number
    ): Values[] {
        const points: Values[] = [start, startStub];

        if (startAxis === 'horizontal') {
            points.push(new Values(startStub.x, bendY));
            points.push(new Values(bendX, bendY));
        } else {
            points.push(new Values(bendX, startStub.y));
            points.push(new Values(bendX, bendY));
        }

        if (endAxis === 'horizontal') {
            points.push(new Values(bendX, endStub.y));
        } else {
            points.push(new Values(endStub.x, bendY));
        }

        points.push(endStub);
        points.push(end);
        return points;
    }

    private buildVerticalStartLPath(
        start: Values,
        end: Values,
        startStub: Values,
        endStub: Values,
        guideX: number,
        guideY: number
    ): Values[] {
        return [
            start,
            startStub,
            new Values(guideX, startStub.y),
            new Values(guideX, guideY),
            new Values(endStub.x, guideY),
            endStub,
            end,
        ];
    }

    private selectBestPath(candidates: Values[][], startBounds: AbsoluteBounds, endBounds: AbsoluteBounds): Values[] {
        let fallbackPath: Values[] = [candidates[0][0], candidates[0][candidates[0].length - 1]];
        let fallbackScore = Number.POSITIVE_INFINITY;
        let bestPath: Values[] | null = null;
        let bestScore = Number.POSITIVE_INFINITY;

        for (const candidate of candidates) {
            const path = this.normalizePath(candidate);
            if (!this.isOrthogonalPath(path)) {
                continue;
            }

            const score = this.calculatePathScore(path);
            if (score < fallbackScore) {
                fallbackPath = path;
                fallbackScore = score;
            }

            if (!this.isPathClear(path, startBounds, endBounds)) {
                continue;
            }

            if (score < bestScore) {
                bestPath = path;
                bestScore = score;
            }
        }

        return bestPath || fallbackPath;
    }

    private selectFirstClearPath(candidates: Values[][], startBounds: AbsoluteBounds, endBounds: AbsoluteBounds): Values[] | null {
        for (const candidate of candidates) {
            const path = this.normalizePath(candidate);
            if (!this.isOrthogonalPath(path) || !this.isPathClear(path, startBounds, endBounds)) {
                continue;
            }
            return path;
        }

        return null;
    }

    private normalizePath(points: Values[]): Values[] {
        const normalized: Values[] = [];
        for (const point of points) {
            const last = normalized[normalized.length - 1];
            if (last && last.x === point.x && last.y === point.y) {
                continue;
            }
            normalized.push(new Values(point.x, point.y));
        }

        const simplified: Values[] = [];
        for (const point of normalized) {
            simplified.push(point);
            while (simplified.length >= 3) {
                const lastIndex = simplified.length - 1;
                const point1 = simplified[lastIndex - 2];
                const point2 = simplified[lastIndex - 1];
                const point3 = simplified[lastIndex];
                if (!this.isPointBetween(point1, point2, point3)) {
                    break;
                }
                simplified.splice(lastIndex - 1, 1);
            }
        }

        return simplified;
    }

    private isPointBetween(point1: Values, point2: Values, point3: Values): boolean {
        if (point1.x === point2.x && point2.x === point3.x) {
            return point2.y >= Math.min(point1.y, point3.y) && point2.y <= Math.max(point1.y, point3.y);
        }

        if (point1.y === point2.y && point2.y === point3.y) {
            return point2.x >= Math.min(point1.x, point3.x) && point2.x <= Math.max(point1.x, point3.x);
        }

        return false;
    }

    private isOrthogonalPath(points: Values[]): boolean {
        if (points.length < 2) {
            return false;
        }

        for (let index = 0; index < points.length - 1; index++) {
            const start = points[index];
            const end = points[index + 1];
            if (start.x !== end.x && start.y !== end.y) {
                return false;
            }
        }

        return true;
    }

    private isPathClear(points: Values[], startBounds: AbsoluteBounds, endBounds: AbsoluteBounds): boolean {
        for (let index = 0; index < points.length - 1; index++) {
            const start = points[index];
            const end = points[index + 1];
            if (this.segmentIntersectsRectInterior(start, end, startBounds)) {
                return false;
            }
            if (this.segmentIntersectsRectInterior(start, end, endBounds)) {
                return false;
            }
        }

        return true;
    }

    private segmentIntersectsRectInterior(start: Values, end: Values, bounds: AbsoluteBounds): boolean {
        if (start.x === end.x) {
            if (start.x <= bounds.left || start.x >= bounds.right) {
                return false;
            }

            const minY = Math.min(start.y, end.y);
            const maxY = Math.max(start.y, end.y);
            return Math.max(minY, bounds.top) < Math.min(maxY, bounds.bottom);
        }

        if (start.y === end.y) {
            if (start.y <= bounds.top || start.y >= bounds.bottom) {
                return false;
            }

            const minX = Math.min(start.x, end.x);
            const maxX = Math.max(start.x, end.x);
            return Math.max(minX, bounds.left) < Math.min(maxX, bounds.right);
        }

        return true;
    }

    private calculatePathScore(points: Values[]): number {
        const bends = Math.max(points.length - 2, 0);
        let length = 0;

        for (let index = 0; index < points.length - 1; index++) {
            length += this.distanceBetween2Points(points[index], points[index + 1]);
        }

        return (bends * 10000) + length;
    }

    private syncSquareSegments() {
        if (this.squarePath.length === 0) {
            this.line1 = new Values(0, 0);
            this.middleLine = new Values(0, 0);
            this.line2 = new Values(0, 0);
            return;
        }

        this.strc = this.squarePath[0];
        this.endc = this.squarePath[this.squarePath.length - 1];
        this.line1 = this.squarePath[1] ? this.squarePath[1] : this.strc;
        this.middleLine = this.squarePath.length > 2 ? this.squarePath[this.squarePath.length - 2] : this.line1;
        this.line2 = this.endc;
    }

    private trimPathForArrowHeads(points: Values[], recess: number): Values[] {
        if (points.length < 2) {
            return points.map((point) => new Values(point.x, point.y));
        }

        const trimmed = points.map((point) => new Values(point.x, point.y));
        trimmed[0] = this.movePointTowards(points[0], points[1], recess);
        trimmed[trimmed.length - 1] = this.movePointTowards(points[trimmed.length - 1], points[trimmed.length - 2], recess);

        return this.normalizePath(trimmed);
    }

    private movePointTowards(origin: Values, target: Values, distance: number): Values {
        const axis = this.getSegmentAxis(origin, target);
        const segmentDistance = this.distanceBetween2Points(origin, target);
        const safeDistance = Math.min(distance, Math.floor(segmentDistance / 2));

        if (axis === 'horizontal') {
            return new Values(origin.x + (Math.sign(target.x - origin.x) * safeDistance), origin.y);
        }

        return new Values(origin.x, origin.y + (Math.sign(target.y - origin.y) * safeDistance));
    }

}
